import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Clock, ArrowRight,
  CheckCircle, XCircle, AlertTriangle, Eye, ShieldAlert,
  TrendingUp, TrendingDown,
} from 'lucide-react';

/**
 * TimelineScrubber — V2 horizontal interactive timeline (G328).
 *
 * Production V2 rebuild of the V1 TimelineScrubber component the
 * Trust Timeline tab originally relied on. Restored after audit
 * confirmed it was completely missing from V2.
 *
 * Features (V1 parity + V2 polish):
 *   - 4 horizontal lanes: Blocked / Warned / Transitions / Allowed
 *   - Shape-coded event markers per V1 conventions:
 *       diamond  = BLOCKED
 *       triangle = WARNED
 *       star     = autonomy TRANSITION (upgrade or downgrade)
 *       circle   = ALLOWED / AUDIT / REDACTED
 *   - Smart time-axis ticks (interval auto-scales to range)
 *   - Connecting polyline through events (visual flow indicator)
 *   - Draggable scrubber w/ time-label callout
 *   - Click-to-select an event marker
 *   - Zoom controls (0.5x → 5x)
 *   - Prev / Next navigation buttons
 *   - **NEW vs V1**: Keyboard navigation (← / → step, + / − zoom)
 *   - Preview panel below shows full event detail
 *   - Empty state when there are no events
 *
 * Pure SVG — no chart library dependency. Caller passes the merged
 * timeline list (with `timestamp` field and either `_kind:'transition'`
 * or `_kind:'enforcement'` for classification). The scrubber stays
 * presentational so the caller controls data fetch + cache.
 *
 * @param {Object} props
 * @param {Array}  props.events          — merged transition + enforcement events
 * @param {string} [props.agentName]
 * @param {string} [props.agentCreatedAt] — extends the time axis to include creation
 */
const LANES = [
  { key: 'BLOCKED',    label: 'Blocked',     idx: 0 },
  { key: 'WARNED',     label: 'Warned',      idx: 1 },
  { key: 'TRANSITION', label: 'Transitions', idx: 2 },
  { key: 'ALLOWED',    label: 'Allowed',     idx: 3 },
];

const EVENT_META = {
  ALLOWED:   { stroke: 'var(--v2-success)', soft: 'var(--v2-success-soft)', icon: CheckCircle,    label: 'Allowed',    shape: 'circle' },
  BLOCKED:   { stroke: 'var(--v2-danger)',  soft: 'var(--v2-danger-soft)',  icon: XCircle,        label: 'Blocked',    shape: 'diamond' },
  WARNED:    { stroke: 'var(--v2-warning)', soft: 'var(--v2-warning-soft)', icon: AlertTriangle,  label: 'Warned',     shape: 'triangle' },
  AUDIT:     { stroke: 'var(--v2-accent-cyan)', soft: 'var(--v2-accent-cyan-soft)', icon: Eye,    label: 'Audit',      shape: 'circle' },
  REDACTED:  { stroke: 'var(--v2-accent-trust)', soft: 'var(--v2-accent-trust-soft)', icon: ShieldAlert, label: 'Redacted', shape: 'circle' },
  UPGRADE:   { stroke: 'var(--v2-success)', soft: 'var(--v2-success-soft)', icon: TrendingUp,     label: 'Upgrade',    shape: 'star' },
  DOWNGRADE: { stroke: 'var(--v2-danger)',  soft: 'var(--v2-danger-soft)',  icon: TrendingDown,   label: 'Downgrade',  shape: 'star' },
};

const AUTONOMY_RANK = { PROBATION: 0, RESTRICTED: 1, HUMAN_CONTROLLED: 1, SUPERVISED: 2, SELF_AUTONOMOUS: 3 };

/* ─── Helpers ────────────────────────────────────────────────────────── */

function classifyEvent(event) {
  if (event._kind === 'transition') {
    const from = AUTONOMY_RANK[event.previousLevel || event.fromLevel] ?? -1;
    const to   = AUTONOMY_RANK[event.newLevel || event.toLevel || event.toAutonomy] ?? -1;
    return to >= from ? 'UPGRADE' : 'DOWNGRADE';
  }
  const d = String(event.decision || event.outcome || 'AUDIT').toUpperCase();
  return EVENT_META[d] ? d : 'AUDIT';
}

function laneForItem(event) {
  if (event._kind === 'transition') return 2;
  const d = String(event.decision || event.outcome || '').toUpperCase();
  if (d === 'BLOCKED') return 0;
  if (d === 'WARNED')  return 1;
  return 3;
}

function fmtTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}
function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
function fmtFull(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} ${fmtTime(ts)}`;
}
function truncHash(h) {
  if (!h) return '—';
  const s = String(h);
  return s.length > 12 ? `${s.slice(0, 8)}…${s.slice(-4)}` : s;
}

/* ─── SVG marker shapes — shape-coded per V1 convention ───────────────── */
function Marker({ x, y, type, active, onClick }) {
  const meta = EVENT_META[type] || EVENT_META.AUDIT;
  const s = active ? 9 : 6;
  const strokeW = active ? 2.5 : 1.5;
  const stroke = active ? 'var(--v2-ink)' : 'var(--v2-bg-surface)';
  const fill = meta.stroke;
  const common = {
    onClick,
    style: {
      cursor: 'pointer',
      transition: 'all 0.15s ease',
      filter: active ? 'drop-shadow(0 0 4px rgba(0,0,0,0.25))' : 'none',
    },
  };

  if (meta.shape === 'diamond') {
    return (
      <g transform={`translate(${x},${y}) rotate(45)`} {...common}>
        <rect x={-s} y={-s} width={s * 2} height={s * 2} rx={1.5}
              fill={fill} stroke={stroke} strokeWidth={strokeW} />
      </g>
    );
  }
  if (meta.shape === 'triangle') {
    const pts = `${x},${y - s * 1.2} ${x - s * 1.1},${y + s * 0.7} ${x + s * 1.1},${y + s * 0.7}`;
    return <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={strokeW} {...common} />;
  }
  if (meta.shape === 'star') {
    const inner = s * 0.45;
    const pts = [];
    for (let i = 0; i < 8; i += 1) {
      const r = i % 2 === 0 ? s * 1.2 : inner;
      const angle = (Math.PI / 4) * i - Math.PI / 2;
      pts.push(`${x + r * Math.cos(angle)},${y + r * Math.sin(angle)}`);
    }
    return <polygon points={pts.join(' ')} fill={fill} stroke={stroke} strokeWidth={strokeW} {...common} />;
  }
  return <circle cx={x} cy={y} r={s} fill={fill} stroke={stroke} strokeWidth={strokeW} {...common} />;
}

/* ─── Preview panel (event detail card) ───────────────────────────────── */
function PreviewPanel({ event, agentName }) {
  if (!event) {
    return (
      <div className="rounded-v2-md bg-v2-muted border border-dashed border-v2-border-subtle p-6 flex flex-col items-center justify-center text-center text-v2-ink-muted text-[12.5px] min-h-[160px]">
        <Clock size={24} className="text-v2-ink-muted opacity-50 mb-2" />
        Drag the scrubber along the timeline, click a marker, or use ← → keys to preview an event.
      </div>
    );
  }
  const type = classifyEvent(event);
  const meta = EVENT_META[type] || EVENT_META.AUDIT;
  const Icon = meta.icon;
  const isTransition = event._kind === 'transition';

  return (
    <div className="rounded-v2-md bg-v2-surface border border-v2-border-subtle overflow-hidden shadow-sm"
         style={{ borderTop: `3px solid ${meta.stroke}`, animation: 'tsFadeIn 0.2s ease' }}>
      <div className="px-4 py-3 flex items-center gap-3" style={{ background: meta.soft }}>
        <div className="w-9 h-9 rounded-v2-md flex items-center justify-center"
             style={{ background: `${meta.stroke}25` }}>
          <Icon size={18} style={{ color: meta.stroke }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-bold text-v2-ink truncate">
            {isTransition ? 'Autonomy transition' : (event.eventType || 'Policy enforcement')}
          </div>
          <div className="text-[11.5px] text-v2-ink-secondary">
            {fmtFull(event.timestamp || event.at || event.createdAt)}
            {agentName && <> · {agentName}</>}
          </div>
        </div>
        <div className="text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-v2-pill border"
             style={{ color: meta.stroke, background: meta.soft, borderColor: `${meta.stroke}30` }}>
          {meta.label}
        </div>
      </div>

      <div className="px-4 py-3">
        {isTransition
          ? <TransitionPreview event={event} />
          : <EnforcementPreview event={event} />}
      </div>
      <style>{`@keyframes tsFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}

function TransitionPreview({ event }) {
  const from = event.previousLevel || event.fromLevel || 'UNSET';
  const to   = event.newLevel || event.toLevel || event.toAutonomy || 'UNSET';
  const isUp = (AUTONOMY_RANK[to] ?? -1) > (AUTONOMY_RANK[from] ?? -1);
  const dirColor = isUp ? 'var(--v2-success)' : 'var(--v2-danger)';
  const score = event.scoreAtTransition ?? event.trustScore;

  return (
    <div>
      <div className="flex items-center gap-3 mb-3 bg-v2-muted rounded-v2-sm px-3 py-2">
        <span className="px-2.5 py-1 rounded-v2-sm bg-v2-surface text-v2-ink text-[12.5px] font-semibold">
          {String(from).replace(/_/g, ' ')}
        </span>
        <ArrowRight size={16} style={{ color: dirColor }} />
        <span className="px-2.5 py-1 rounded-v2-sm text-[12.5px] font-bold"
              style={{ background: `${dirColor}15`, color: dirColor }}>
          {String(to).replace(/_/g, ' ')}
        </span>
        {score != null && (
          <span className="ml-auto text-[22px] font-bold tabular-nums" style={{ color: dirColor }}>
            {Math.round(Number(score))}
          </span>
        )}
      </div>
      <DetailGrid items={[
        ['Trigger',       event.triggerType || '—'],
        ['Reason',        event.triggerReason || event.reason || '—'],
        ['Decided by',    event.decidedBy || event.actorEmail || 'System'],
        ['Score',         score != null ? Math.round(Number(score)) : '—'],
        ['Compliance',    event.complianceScore != null ? event.complianceScore : '—'],
        ['Hash',          truncHash(event.transitionHash || event.eventHash), true],
      ]} />
    </div>
  );
}

function EnforcementPreview({ event }) {
  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        {event.skillName && <Chip label="Skill" value={event.skillName} />}
        {event.evaluationTimeMs != null && <Chip label="Eval" value={`${event.evaluationTimeMs} ms`} />}
        {event.ruleId && <Chip label="Rule" value={truncHash(event.ruleId)} mono />}
        {event.ruleVersion != null && <Chip label="Version" value={`v${event.ruleVersion}`} />}
      </div>
      <DetailGrid items={[
        ['Event type', event.eventType || 'POLICY_RULE_EVALUATION'],
        ['Skill ID',   event.skillId ? truncHash(event.skillId) : '—', true],
        ['Workflow',   event.workflowId ? truncHash(event.workflowId) : event.workflowExecutionId ? truncHash(event.workflowExecutionId) : '—', true],
        ['Hash',       truncHash(event.eventHash), true],
      ]} />
      {event.executionDetail && (
        <details className="mt-3">
          <summary className="text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted cursor-pointer">
            Execution details
          </summary>
          <pre className="mt-2 bg-v2-ink/95 text-v2-surface rounded-v2-sm p-3 text-[10.5px] font-mono-v2 overflow-auto max-h-[160px]"
               style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {typeof event.executionDetail === 'string'
              ? event.executionDetail
              : JSON.stringify(event.executionDetail, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

function DetailGrid({ items }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-2 text-[11.5px]">
      {items.map(([label, value, mono]) => (
        <div key={label} className={mono ? 'col-span-1' : 'col-span-1'}>
          <dt className="text-[10px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-0.5">{label}</dt>
          <dd className={`text-v2-ink m-0 break-all ${mono ? 'font-mono-v2 text-[10.5px]' : ''}`}>
            {value == null || value === '' ? '—' : String(value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function Chip({ label, value, mono }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-v2-muted rounded-v2-sm text-[11.5px]">
      <span className="font-semibold text-v2-ink-muted">{label}:</span>
      <span className={`text-v2-ink ${mono ? 'font-mono-v2 text-[10.5px]' : ''}`}>{value}</span>
    </span>
  );
}

/* ─── Main scrubber ──────────────────────────────────────────────────── */
export function TimelineScrubber({ events, agentName, agentCreatedAt }) {
  const containerRef = useRef(null);
  const rootRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [scrubX, setScrubX] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  // Layout constants
  const PADDING_LEFT = 100;
  const PADDING_RIGHT = 40;
  const LANE_HEIGHT = 36;
  const LANE_COUNT = LANES.length;
  const SVG_HEIGHT = 24 + LANE_COUNT * LANE_HEIGHT + 40;
  const BASE_WIDTH = 820;
  const svgWidth = Math.max(BASE_WIDTH, BASE_WIDTH * zoomLevel);
  const trackLeft = PADDING_LEFT;
  const trackRight = svgWidth - PADDING_RIGHT;
  const trackWidth = trackRight - trackLeft;

  /* ── Time extent — padded so the first/last events don't sit on the
   * edge. Also pulls in agentCreatedAt if supplied. */
  const timeExtent = useMemo(() => {
    if (!events || events.length === 0) {
      return { min: Date.now() - 86_400_000, max: Date.now() };
    }
    const times = events.map((e) => new Date(e.timestamp || e.at || e.createdAt).getTime()).filter(Number.isFinite);
    if (agentCreatedAt) {
      const t = new Date(agentCreatedAt).getTime();
      if (Number.isFinite(t)) times.push(t);
    }
    if (times.length === 0) return { min: Date.now() - 86_400_000, max: Date.now() };
    const min = Math.min(...times);
    const max = Math.max(...times);
    const pad = Math.max((max - min) * 0.05, 60_000);
    return { min: min - pad, max: max + pad };
  }, [events, agentCreatedAt]);

  const timeToX = useCallback((ts) => {
    const t = new Date(ts).getTime();
    const pct = (t - timeExtent.min) / (timeExtent.max - timeExtent.min);
    return trackLeft + pct * trackWidth;
  }, [timeExtent, trackLeft, trackWidth]);

  const xToTime = useCallback((x) => {
    const pct = (x - trackLeft) / trackWidth;
    return timeExtent.min + pct * (timeExtent.max - timeExtent.min);
  }, [timeExtent, trackLeft, trackWidth]);

  /* Positioned events — each with x, lane index, event type. */
  const positionedEvents = useMemo(() => {
    if (!events) return [];
    return events.map((ev, idx) => ({
      ...ev,
      _idx: idx,
      _x: timeToX(ev.timestamp || ev.at || ev.createdAt),
      _lane: laneForItem(ev),
      _type: classifyEvent(ev),
    }));
  }, [events, timeToX]);

  const findNearest = useCallback((px) => {
    if (positionedEvents.length === 0) return null;
    let closest = null;
    let minDist = Infinity;
    for (const ev of positionedEvents) {
      const d = Math.abs(ev._x - px);
      if (d < minDist) { minDist = d; closest = ev; }
    }
    return closest;
  }, [positionedEvents]);

  /* ── Mouse drag ─────────────────────────────────────────────────────── */
  const getSvgX = useCallback((clientX) => {
    if (!containerRef.current) return 0;
    const rect = containerRef.current.getBoundingClientRect();
    return clientX - rect.left + containerRef.current.scrollLeft;
  }, []);

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    const x = getSvgX(e.clientX);
    setScrubX(x);
    const n = findNearest(x);
    if (n) setActiveIndex(n._idx);
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      const x = getSvgX(e.clientX);
      setScrubX(x);
      const n = findNearest(x);
      if (n) setActiveIndex(n._idx);
    };
    const onUp = () => setIsDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, getSvgX, findNearest]);

  /* ── Navigation ─────────────────────────────────────────────────────── */
  const goToEvent = useCallback((direction) => {
    if (positionedEvents.length === 0) return;
    let next;
    if (activeIndex === null) {
      next = direction > 0 ? 0 : positionedEvents.length - 1;
    } else {
      next = Math.max(0, Math.min(positionedEvents.length - 1, activeIndex + direction));
    }
    setActiveIndex(next);
    const ev = positionedEvents.find((e) => e._idx === next);
    if (ev) setScrubX(ev._x);
  }, [activeIndex, positionedEvents]);

  const zoom = useCallback((delta) => {
    setZoomLevel((z) => Math.max(0.5, Math.min(5, z + delta)));
  }, []);

  /* ── G328 — Keyboard navigation (new in V2). ←/→ to step events,
   * +/-/= to zoom. Only active when the scrubber root has focus or
   * receives a key event from inside it. */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const onKey = (e) => {
      // Only intercept when focused inside the scrubber
      if (!root.contains(document.activeElement) && document.activeElement !== root) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); goToEvent(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); goToEvent(-1); }
      else if (e.key === '+' || e.key === '=') { e.preventDefault(); zoom(0.25); }
      else if (e.key === '-' || e.key === '_') { e.preventDefault(); zoom(-0.25); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goToEvent, zoom]);

  /* Smart-interval time axis ticks */
  const axisTicks = useMemo(() => {
    const range = timeExtent.max - timeExtent.min;
    const targetTicks = Math.floor(trackWidth / 120);
    const rawInterval = range / Math.max(targetTicks, 1);
    const niceIntervals = [60_000, 300_000, 600_000, 1_800_000, 3_600_000, 7_200_000, 21_600_000, 43_200_000, 86_400_000, 604_800_000];
    const interval = niceIntervals.find((n) => n >= rawInterval) || rawInterval;
    const ticks = [];
    const start = Math.ceil(timeExtent.min / interval) * interval;
    for (let t = start; t <= timeExtent.max; t += interval) {
      ticks.push({ t, x: timeToX(t), label: interval >= 86_400_000 ? fmtDate(t) : fmtTime(t) });
    }
    return ticks;
  }, [timeExtent, trackWidth, timeToX]);

  const scrubberX = scrubX ?? (activeIndex !== null
    ? positionedEvents.find((e) => e._idx === activeIndex)?._x ?? null
    : null);
  const activeEvent = activeIndex !== null ? positionedEvents[activeIndex] : null;

  /* Empty state */
  if (!events || events.length === 0) {
    return (
      <div className="rounded-v2-md bg-v2-muted border border-dashed border-v2-border-subtle p-6 text-center text-v2-ink-muted text-[12.5px]">
        No events to display on the timeline. Run agent workflows or wait for the next
        scheduled scan to generate trust events.
      </div>
    );
  }

  return (
    <div ref={rootRef} tabIndex={0} className="outline-none focus-visible:ring-2 focus-visible:ring-v2-action/30 rounded-v2-md">
      {/* Controls bar */}
      <div className="flex items-center gap-2 mb-2.5 px-1">
        <button onClick={() => goToEvent(-1)}
                className="w-7 h-7 rounded-v2-sm border border-v2-border-subtle bg-v2-surface text-v2-ink-secondary hover:bg-v2-muted flex items-center justify-center"
                title="Previous event (←)">
          <ChevronLeft size={14} />
        </button>
        <button onClick={() => goToEvent(1)}
                className="w-7 h-7 rounded-v2-sm border border-v2-border-subtle bg-v2-surface text-v2-ink-secondary hover:bg-v2-muted flex items-center justify-center"
                title="Next event (→)">
          <ChevronRight size={14} />
        </button>

        <span className="text-[11px] text-v2-ink-muted font-medium ml-1">
          {activeIndex !== null
            ? `Event ${activeIndex + 1} of ${events.length}`
            : `${events.length} events`}
        </span>

        <div className="w-px h-4 bg-v2-border-subtle mx-2" />

        <button onClick={() => zoom(-0.25)}
                className="w-7 h-7 rounded-v2-sm border border-v2-border-subtle bg-v2-surface text-v2-ink-secondary hover:bg-v2-muted flex items-center justify-center"
                title="Zoom out (−)">
          <ZoomOut size={14} />
        </button>
        <span className="text-[10.5px] text-v2-ink-muted min-w-[36px] text-center font-mono-v2">
          {Math.round(zoomLevel * 100)}%
        </span>
        <button onClick={() => zoom(0.25)}
                className="w-7 h-7 rounded-v2-sm border border-v2-border-subtle bg-v2-surface text-v2-ink-secondary hover:bg-v2-muted flex items-center justify-center"
                title="Zoom in (+)">
          <ZoomIn size={14} />
        </button>

        {/* Legend */}
        <div className="ml-auto flex items-center gap-3 text-[10.5px] text-v2-ink-muted">
          <LegendDot color="var(--v2-danger)"  label="Block" />
          <LegendDot color="var(--v2-warning)" label="Warn" />
          <LegendDot color="var(--v2-success)" label="Allow" />
          <LegendDot color="var(--v2-accent-trust)" label="Transition" />
        </div>
      </div>

      {/* Scrollable SVG container */}
      <div
        ref={containerRef}
        onMouseDown={onMouseDown}
        className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-sm overflow-x-auto overflow-y-hidden select-none"
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <svg width={svgWidth} height={SVG_HEIGHT} style={{ display: 'block' }}>
          {/* Lane backgrounds + labels + dividers */}
          {LANES.map((lane, i) => (
            <g key={lane.key}>
              <rect x={0} y={24 + i * LANE_HEIGHT}
                    width={svgWidth} height={LANE_HEIGHT}
                    fill={i % 2 === 0 ? 'var(--v2-bg-muted)' : 'var(--v2-bg-surface)'} />
              <text x={8} y={24 + i * LANE_HEIGHT + LANE_HEIGHT / 2 + 4}
                    fontSize={10} fontWeight={600}
                    fill="var(--v2-text-muted)"
                    style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {lane.label}
              </text>
              <line x1={trackLeft} y1={24 + (i + 1) * LANE_HEIGHT}
                    x2={trackRight} y2={24 + (i + 1) * LANE_HEIGHT}
                    stroke="var(--v2-border-subtle)" strokeWidth={1} />
            </g>
          ))}

          {/* Time axis ticks */}
          {axisTicks.map((tick, i) => (
            <g key={i}>
              <line x1={tick.x} y1={24} x2={tick.x} y2={24 + LANE_COUNT * LANE_HEIGHT}
                    stroke="var(--v2-border-subtle)" strokeWidth={1} strokeDasharray="2 3" />
              <text x={tick.x} y={24 + LANE_COUNT * LANE_HEIGHT + 16}
                    textAnchor="middle" fontSize={9} fill="var(--v2-text-muted)">
                {tick.label}
              </text>
            </g>
          ))}

          {/* Connecting polyline (gives a "flow" sense between successive events) */}
          {positionedEvents.length > 1 && (
            <polyline
              fill="none"
              stroke="var(--v2-border-subtle)" strokeWidth={1}
              points={positionedEvents.map((ev) =>
                `${ev._x},${24 + ev._lane * LANE_HEIGHT + LANE_HEIGHT / 2}`).join(' ')}
            />
          )}

          {/* Event markers */}
          {positionedEvents.map((ev) => (
            <Marker
              key={ev._idx}
              x={ev._x}
              y={24 + ev._lane * LANE_HEIGHT + LANE_HEIGHT / 2}
              type={ev._type}
              active={ev._idx === activeIndex}
              onClick={(e) => {
                e.stopPropagation();
                setActiveIndex(ev._idx);
                setScrubX(ev._x);
              }}
            />
          ))}

          {/* Scrubber line + handle + time label */}
          {scrubberX != null && (
            <g style={{ pointerEvents: 'none' }}>
              <line x1={scrubberX} y1={16}
                    x2={scrubberX} y2={24 + LANE_COUNT * LANE_HEIGHT + 4}
                    stroke="var(--v2-action)" strokeWidth={1.5} strokeDasharray="4 2" />
              <g transform={`translate(${scrubberX}, 12)`}>
                <polygon points="0,-6 -5,2 5,2" fill="var(--v2-action)" />
              </g>
              <g transform={`translate(${scrubberX}, 6)`}>
                <rect x={-40} y={-9} width={80} height={15} rx={4} fill="var(--v2-action)" />
                <text x={0} y={2} textAnchor="middle"
                      fontSize={9} fill="#fff" fontWeight={600}>
                  {fmtTime(xToTime(scrubberX))}
                </text>
              </g>
            </g>
          )}
        </svg>
      </div>

      {/* Helper hint when no event is yet selected */}
      {activeIndex === null && (
        <p className="text-[10.5px] text-v2-ink-muted mt-1.5 px-1">
          Tip: click anywhere on the timeline to scrub, or use ← / → keys to step through events.
        </p>
      )}

      {/* Preview panel */}
      <div className="mt-3">
        <PreviewPanel event={activeEvent} agentName={agentName} />
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

export default TimelineScrubber;
